/**
 * Google Calendar Service - Modern Implementation
 * Using @react-oauth/google for authentication and Google Calendar API v3
 */

import { getApiEndpoint } from "../config/config";

// Store access token globally for API calls
let accessToken = null;

/**
 * Set the access token received from OAuth
 * @param {string} token - The OAuth access token
 */
export const setAccessToken = token => {
  accessToken = token;
};

/**
 * Get the current access token
 * @returns {string|null} The access token or null
 */
export const getAccessToken = () => {
  return accessToken;
};

/**
 * Check if user is signed in (has valid access token)
 * @returns {boolean} True if signed in, false otherwise
 */
export const isSignedIn = () => {
  return accessToken !== null;
};

/**
 * Sign out - clear the access token
 */
export const signOut = () => {
  accessToken = null;
};

/**
 * Load Google Calendar token from backend
 * @param {string} jwt - JWT token for authentication
 * @returns {Promise<boolean>} True if token was loaded successfully
 */
export const loadTokenFromBackend = async jwt => {
  // try {
  //   const response = await fetch(
  //     getApiEndpoint("/user/google-calendar/status"),
  //     {
  //       method: "GET",
  //       headers: {
  //         "Content-Type": "application/json",
  //         Authorization: `Bearer ${jwt}`,
  //       },
  //     }
  //   );
  //   if (response.ok) {
  //     const result = await response.json();
  //     if (result.data.connected && result.data.token) {
  //       accessToken = result.data.token;
  //       return true;
  //     }
  //   }
  return false;
  // } catch (error) {
  //   return false;
  // }
};

/**
 * Save Google Calendar token to backend
 * @param {string} jwt - JWT token for authentication
 * @param {string} token - Google Calendar access token or authorization code
 * @param {string} refreshToken - Optional refresh token
 * @returns {Promise<boolean>} True if token was saved successfully
 */
export const saveTokenToBackend = async (jwt, token, refreshToken = null) => {
  // try {
  //   const body = { access_token: token };
  //   if (refreshToken) {
  //     body.refresh_token = refreshToken;
  //   }
  //   const response = await fetch(
  //     getApiEndpoint("/user/google-calendar/connect"),
  //     {
  //       method: "POST",
  //       headers: {
  //         "Content-Type": "application/json",
  //         Authorization: `Bearer ${jwt}`,
  //       },
  //       body: JSON.stringify(body),
  //     }
  //   );
  //   if (response.ok) {
  //     return true;
  //   }
  return false;
  // } catch (error) {
  //   return false;
  // }
};

/**
 * Remove Google Calendar token from backend
 * @param {string} jwt - JWT token for authentication
 * @returns {Promise<boolean>} True if token was removed successfully
 */
export const removeTokenFromBackend = async jwt => {
  // try {
  // const response = await fetch(
  //   getApiEndpoint("/user/google-calendar/disconnect"),
  //   {
  //     method: "POST",
  //     headers: {
  //       "Content-Type": "application/json",
  //       Authorization: `Bearer ${jwt}`,
  //     },
  //   }
  // );

  // if (response.ok) {
  //   return true;
  // }
  return false;
  // } catch (error) {
  //   return false;
  // }
};

/**
 * Get Google Calendar connection status from backend
 * @param {string} jwt - JWT token for authentication
 * @returns {Promise<object>} Connection status object
 */
export const getConnectionStatus = async jwt => {
  try {
    const response = await fetch(
      getApiEndpoint("/user/google-calendar/status"),
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${jwt}`,
        },
      }
    );

    if (response.ok) {
      const result = await response.json();
      return result.data;
    }
    return { connected: false, connected_at: null, token: null };
  } catch (error) {
    return { connected: false, connected_at: null, token: null };
  }
};

/**
 * Refresh the Google Calendar access token
 * @param {string} jwt - JWT token for authentication
 * @returns {Promise<string|null>} New access token or null
 */
const refreshAccessToken = async jwt => {
  try {
    const response = await fetch(
      getApiEndpoint("/user/google-calendar/refresh-token"),
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${jwt}`,
        },
      }
    );

    if (response.ok) {
      const result = await response.json();
      if (result.data?.access_token) {
        accessToken = result.data.access_token;
        return result.data.access_token;
      }
    }
    return null;
  } catch (error) {
    return null;
  }
};

/**
 * Make an authenticated request to Google Calendar API with automatic retry on 401
 * @param {string} endpoint - The API endpoint
 * @param {object} options - Fetch options
 * @param {string} jwt - Optional JWT for token refresh
 * @returns {Promise<any>} The API response
 */
const makeCalendarRequest = async (endpoint, options = {}, jwt = null) => {
  if (!accessToken) {
    throw new Error("Not authenticated. Please sign in first.");
  }

  const url = `https://www.googleapis.com/calendar/v3/${endpoint}`;

  const response = await fetch(url, {
    ...options,
    headers: {
      ...options.headers,
      Authorization: `Bearer ${accessToken}`,
      "Content-Type": "application/json",
    },
  });

  // If we get a 401 and have a JWT, try to refresh the token and retry once
  if (response.status === 401 && jwt && !options._retried) {
    const newToken = await refreshAccessToken(jwt);

    if (newToken) {
      // Retry the request with the new token
      return makeCalendarRequest(endpoint, { ...options, _retried: true }, jwt);
    } else {
      // Token refresh failed, clear the token
      accessToken = null;
      throw new Error(
        "Authentication expired. Please reconnect Google Calendar."
      );
    }
  }

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    const errorMessage = error.error?.message || response.statusText;
    throw new Error(`Calendar API error: ${errorMessage}`);
  }

  return response.json();
};

/**
 * Check for conflicting events in the calendar
 * @param {string} startDateTime - Start time in ISO format
 * @param {string} endDateTime - End time in ISO format
 * @param {string} jwt - Optional JWT for token refresh
 * @returns {Promise<Array>} Array of conflicting events
 */
export const checkForConflicts = async (
  startDateTime,
  endDateTime,
  jwt = null
) => {
  try {
    if (!isSignedIn()) {
      throw new Error("Not authenticated. Please sign in first.");
    }

    const queryParams = new URLSearchParams({
      timeMin: startDateTime,
      timeMax: endDateTime,
      showDeleted: "false",
      singleEvents: "true",
      orderBy: "startTime",
    });

    const response = await makeCalendarRequest(
      `calendars/primary/events?${queryParams}`,
      {},
      jwt
    );

    return response.items || [];
  } catch (error) {
    throw error;
  }
};

/**
 * Create a calendar event
 * @param {object} eventDetails - Event details
 * @param {string} jwt - Optional JWT for token refresh
 * @returns {Promise<object>} The created event with success status
 */
export const createCalendarEvent = async (eventDetails, jwt = null) => {
  try {
    // Check if user is authenticated
    if (!isSignedIn()) {
      throw new Error(
        "Not authenticated. Please sign in to Google Calendar first."
      );
    }

    // Check for conflicts before creating the event
    const conflicts = await checkForConflicts(
      eventDetails.startDateTime,
      eventDetails.endDateTime,
      jwt
    );

    if (conflicts.length > 0) {
      const conflictTitles = conflicts
        .map(e => e.summary || "Untitled Event")
        .join(", ");
      throw new Error(
        `This time slot conflicts with existing event(s): ${conflictTitles}. Please choose a different time.`
      );
    }

    const {
      summary,
      description,
      location,
      startDateTime,
      endDateTime,
      attendees,
      recruitId,
      managerIds,
      managerNames,
      timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone,
    } = eventDetails;

    // Validate required fields
    if (!summary || !startDateTime || !endDateTime) {
      throw new Error(
        "Missing required fields: summary, startDateTime, or endDateTime"
      );
    }

    // Format attendees array
    const formattedAttendees = attendees
      ? attendees
          .split(",")
          .map(email => email.trim())
          .filter(email => email)
          .map(email => ({ email }))
      : [];

    // Create event object
    const event = {
      summary,
      description: description || "",
      location: location || "",
      start: {
        dateTime: startDateTime,
        timeZone,
      },
      end: {
        dateTime: endDateTime,
        timeZone,
      },
      attendees: formattedAttendees,
      reminders: {
        useDefault: false,
        overrides: [
          { method: "email", minutes: 24 * 60 },
          { method: "popup", minutes: 10 },
        ],
      },
    };

    // Add extended properties if recruitId is provided
    if (recruitId) {
      event.extendedProperties = {
        private: {
          recruitId: String(recruitId),
          source: "reppi-recruiting",
        },
      };

      // Add manager info if provided (comma-separated for multiple managers)
      if (managerIds) {
        event.extendedProperties.private.managerIds = managerIds;
      }
      if (managerNames) {
        event.extendedProperties.private.managerNames = managerNames;
      }
    }

    // Insert the event with timeout
    const response = await Promise.race([
      makeCalendarRequest(
        "calendars/primary/events?sendUpdates=all",
        {
          method: "POST",
          body: JSON.stringify(event),
        },
        jwt
      ),
      new Promise((_, reject) =>
        setTimeout(
          () =>
            reject(
              new Error("Calendar API request timed out after 30 seconds")
            ),
          30000
        )
      ),
    ]);

    return {
      success: true,
      event: response,
      htmlLink: response.htmlLink,
    };
  } catch (error) {
    // Provide more specific error messages
    if (error.message.includes("401") || error.message.includes("403")) {
      throw new Error(
        "Authentication failed. Please sign in again and grant calendar permissions."
      );
    } else if (error.message.includes("404")) {
      throw new Error(
        "Google Calendar API not found. Please ensure the Calendar API is enabled in Google Cloud Console."
      );
    } else if (error.message.includes("409")) {
      throw new Error(
        "This time slot conflicts with an existing event. Please choose a different time."
      );
    } else if (error.message.includes("timed out")) {
      throw new Error(
        "Request timed out. Please check your internet connection and try again."
      );
    } else if (!navigator.onLine) {
      throw new Error(
        "No internet connection. Please check your connection and try again."
      );
    } else {
      throw new Error(
        `Failed to create calendar event: ${error.message}. Please try again or contact support.`
      );
    }
  }
};

/**
 * Get user's calendar list
 * @param {string} jwt - Optional JWT for token refresh
 * @returns {Promise<Array>} List of calendars
 */
export const getCalendarList = async (jwt = null) => {
  try {
    if (!isSignedIn()) {
      throw new Error("Not authenticated. Please sign in first.");
    }

    const response = await makeCalendarRequest(
      "users/me/calendarList",
      {},
      jwt
    );
    return response.items || [];
  } catch (error) {
    throw error;
  }
};

/**
 * Get upcoming events
 * @param {number} maxResults - Maximum number of events to return
 * @param {string} jwt - Optional JWT for token refresh
 * @returns {Promise<Array>} List of upcoming events
 */
export const getUpcomingEvents = async (maxResults = 10, jwt = null) => {
  try {
    if (!isSignedIn()) {
      throw new Error("Not authenticated. Please sign in first.");
    }

    const timeMin = new Date().toISOString();
    const queryParams = new URLSearchParams({
      timeMin,
      showDeleted: "false",
      singleEvents: "true",
      maxResults: maxResults.toString(),
      orderBy: "startTime",
    });

    const response = await makeCalendarRequest(
      `calendars/primary/events?${queryParams}`,
      {},
      jwt
    );
    return response.items || [];
  } catch (error) {
    throw error;
  }
};

/**
 * Get events for a specific date range
 * @param {Date} startDate - Start date
 * @param {Date} endDate - End date
 * @param {string} jwt - Optional JWT for token refresh
 * @returns {Promise<Array>} List of events in the date range
 */
export const getEventsForDateRange = async (startDate, endDate, jwt = null) => {
  try {
    if (!isSignedIn()) {
      throw new Error("Not authenticated. Please sign in first.");
    }

    const queryParams = new URLSearchParams({
      timeMin: startDate.toISOString(),
      timeMax: endDate.toISOString(),
      showDeleted: "false",
      singleEvents: "true",
      orderBy: "startTime",
    });

    const response = await makeCalendarRequest(
      `calendars/primary/events?${queryParams}`,
      {},
      jwt
    );
    return response.items || [];
  } catch (error) {
    throw error;
  }
};

/**
 * Delete a calendar event
 * @param {string} eventId - The ID of the event to delete
 * @param {string} jwt - Optional JWT for token refresh
 * @returns {Promise<void>}
 */
export const deleteCalendarEvent = async (eventId, jwt = null) => {
  try {
    if (!isSignedIn()) {
      throw new Error("Not authenticated. Please sign in first.");
    }

    await makeCalendarRequest(
      `calendars/primary/events/${eventId}`,
      {
        method: "DELETE",
      },
      jwt
    );
  } catch (error) {
    throw error;
  }
};

/**
 * Update a calendar event
 * @param {string} eventId - The ID of the event to update
 * @param {object} eventDetails - Updated event details
 * @param {string} jwt - Optional JWT for token refresh
 * @returns {Promise<object>} The updated event
 */
export const updateCalendarEvent = async (
  eventId,
  eventDetails,
  jwt = null
) => {
  try {
    if (!isSignedIn()) {
      throw new Error("Not authenticated. Please sign in first.");
    }

    const {
      summary,
      description,
      location,
      startDateTime,
      endDateTime,
      attendees,
      timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone,
    } = eventDetails;

    const formattedAttendees = attendees
      ? attendees
          .split(",")
          .map(email => email.trim())
          .filter(email => email)
          .map(email => ({ email }))
      : [];

    const event = {
      summary,
      description: description || "",
      location: location || "",
      start: {
        dateTime: startDateTime,
        timeZone,
      },
      end: {
        dateTime: endDateTime,
        timeZone,
      },
      attendees: formattedAttendees,
    };

    const response = await makeCalendarRequest(
      `calendars/primary/events/${eventId}`,
      {
        method: "PUT",
        body: JSON.stringify(event),
      },
      jwt
    );

    return response;
  } catch (error) {
    throw error;
  }
};

/**
 * Get calendar events for a specific user by user season ID
 * @param {number} userSeasonId - The user season ID
 * @param {Date} startDate - Start date
 * @param {Date} endDate - End date
 * @param {string} jwt - JWT token for authentication
 * @returns {Promise<Array>} List of events in the date range
 */
export const getEventsForUserBySeason = async (
  userSeasonId,
  startDate,
  endDate,
  jwt
) => {
  try {
    const response = await fetch(
      getApiEndpoint(`/user/google-calendar/events/${userSeasonId}`),
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${jwt}`,
        },
        body: JSON.stringify({
          startDate: startDate.toISOString(),
          endDate: endDate.toISOString(),
        }),
      }
    );

    if (response.ok) {
      const result = await response.json();
      // Return both events and google_email
      return {
        events: result.data.events || [],
        google_email: result.data.google_email || null,
      };
    } else if (response.status === 404) {
      // User hasn't connected their Google Calendar
      return null;
    } else {
      throw new Error("Failed to fetch calendar events for user");
    }
  } catch (error) {
    // Return null instead of throwing to allow graceful degradation
    return null;
  }
};

/**
 * Create a calendar event for a specific user by user season ID
 * @param {number} userSeasonId - The user season ID
 * @param {object} eventDetails - Event details
 * @param {string} jwt - JWT token for authentication
 * @returns {Promise<object>} The created event
 */
export const createEventForUserBySeason = async (
  userSeasonId,
  eventDetails,
  jwt
) => {
  try {
    const response = await fetch(
      getApiEndpoint(`/user/google-calendar/create-event/${userSeasonId}`),
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${jwt}`,
        },
        body: JSON.stringify(eventDetails),
      }
    );

    if (response.ok) {
      const result = await response.json();
      return result.data.event || null;
    } else if (response.status === 404) {
      // User hasn't connected their Google Calendar
      return null;
    } else {
      throw new Error("Failed to create calendar event for user");
    }
  } catch (error) {
    // Return null instead of throwing to allow graceful degradation
    return null;
  }
};
